import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  pageType: 'WebSite' | 'WebPage' | 'Article' | 'AboutPage';
  publishDate?: string;
  articleCategory?: string;
  articleImage?: string;
  authorName?: string;
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  pageType,
  publishDate,
  articleCategory,
  articleImage,
  authorName,
}: SEOProps) {
  const fullUrl = `https://blissstream.github.io${canonicalUrl}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) {
      canonicalEl.setAttribute('href', fullUrl);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = fullUrl;
      document.head.appendChild(link);
    }

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:type', pageType === 'Article' ? 'article' : 'website', true);
    setMeta('og:site_name', '悦己新知', true);
    setMeta('og:locale', 'zh_CN', true);
    if (articleImage) {
      setMeta('og:image', articleImage, true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    const existingScript = document.querySelector('script[data-schema="seo"]');
    if (existingScript) existingScript.remove();

    const schemas = buildSchemas(title, description, fullUrl, pageType, publishDate, articleCategory, authorName);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'seo');
    script.textContent = JSON.stringify(schemas);
    document.head.appendChild(script);
  }, [title, description, keywords, canonicalUrl, fullUrl, pageType, publishDate, articleCategory, articleImage, authorName]);

  return null;
}

function buildSchemas(
  title: string,
  description: string,
  fullUrl: string,
  pageType: string,
  publishDate?: string,
  articleCategory?: string,
  authorName?: string,
) {
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '悦己新知',
    url: 'https://blissstream.github.io',
    description: '专注于女性性健康科普的知识平台，科学解析自慰与潮喷的生理原理。',
    email: 'hello@yuejixinzhi.com',
    foundingDate: '2024',
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '悦己新知',
    url: 'https://blissstream.github.io',
    description: '专注于女性性健康科普的知识平台',
  };

  const pageTitle = title.split('|')[0].trim();

  const pageObject: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': pageType,
    name: pageTitle,
    description: description,
    url: fullUrl,
  };

  if (pageType === 'Article') {
    pageObject.headline = pageTitle;
    if (publishDate) pageObject.datePublished = publishDate;
    if (publishDate) pageObject.dateModified = publishDate;
    if (articleCategory) pageObject.articleSection = articleCategory;
    if (authorName) {
      pageObject.author = {
        '@type': 'Person',
        name: authorName,
      };
    }
  }

  return [orgSchema, websiteSchema, pageObject];
}